import PropTypes from 'prop-types';
import { Tooltip } from '@mui/material';
import React, { useContext } from 'react';

import FullstackIcon from '../../images/fullstack.svg';
import WarningIcon from '../../images/warningIcon.svg';
import ProjectContext from '../../context/ProjectContext';
import DoneIcon from '../../images/doneStatus.svg';
import BlockedIcon from '../../images/blockedStatus.svg';
import SkillStatusDialog from '../CustomRenderer/skillStatusDialog';
import TeamMembersContext from '../../context/TeamMembersContext';
import { skillKeyToSkillName } from './column';
import { AvatarImage } from '../Avatar';

const propTypes = {
  value: PropTypes.array,
  data: PropTypes.oneOfType([PropTypes.object]),
  colDef: PropTypes.oneOfType([PropTypes.object]),
  rowIndex: PropTypes.number,
};

const defaultProps = {
  value: [],
  data: {},
  colDef: {},
};

const AgGridCell = React.memo((props) => {
  const { data, colDef, rowIndex } = props;
  let { value } = props;
  const sprint = colDef.field;
  const propData = { sprint, data };
  const { showTeamMembers } = useContext(TeamMembersContext);
  const labels = [];
  const showUserUnavailable = [];

  if (propData?.data[sprint]) {
    propData?.data[sprint]?.forEach((user) => {
      const label = user[`${sprint}_labelname`];
      labels.push(label);

      showUserUnavailable.push(user[`${sprint}_is_userUnavailable`]);
    });

    const labelString = labels.join(', ');

    value = labelString;
  }

  const skillName = showTeamMembers ? value : data[`${sprint}_skillName`];
  const skill = data[`${sprint}_skill`];
  const { tooltipDisplayEpicId, tooltipSummary } = propData.data;

  const toolTipEpicData = React.useMemo(() => {
    let title = '';
    if (tooltipDisplayEpicId && tooltipSummary) {
      title = (
        <div>
          <p>
            <strong> EpicId: </strong>
            {tooltipDisplayEpicId}
          </p>
          <p>
            <strong> Summary: </strong>
            {tooltipSummary}
          </p>
          <p>
            <strong> Skill with name: </strong>
            {value}
          </p>
        </div>
      );
    }
    return title;
  }, [tooltipDisplayEpicId, tooltipSummary, value]);

  const { duration: sprintDuration, startDate: sprintStartDate } = useContext(ProjectContext);
  let showFullStackIcon = false;

  if (value) {
    if (value.includes('Full Stack')) {
      showFullStackIcon = true;
    } else if (value.includes('Front End')) {
      propData?.data[sprint]?.forEach((user) => {
        const { isFrontEndByFullstack } = user;
        if (isFrontEndByFullstack) {
          showFullStackIcon = true;
        }
      });
    } else if (value.includes('Back End')) {
      propData?.data[sprint]?.forEach((user) => {
        const { isBackEndByFullstack } = user;
        if (isBackEndByFullstack) {
          showFullStackIcon = true;
        }
      });
    }
  }

  const showDelayedIcon = data[`${sprint}_is_delayed`];

  const tooltipTitle = React.useMemo(() => {
    let title = '';
    if (showDelayedIcon && showUserUnavailable.includes(true)) {
      title = (
        <ul style={{ paddingLeft: '10px', margin: 0 }}>
          <li>Delayed</li>
          <li>User Unavailable</li>
        </ul>
      );
    } else if (showUserUnavailable.includes(true)) {
      title = 'User Unavailable';
    } else if (showDelayedIcon === true) {
      title = 'Delayed';
    }
    return title;
  }, [showDelayedIcon, showUserUnavailable]);

  const toolTipEpicProps = {
    tooltip: {
      sx: {
        background: '#344054',
        color: '#ffffff',
        width: '250px',
        zIndex: '2',
        fontSize: '12px',
        textAlign: 'justify',
        height: 'auto !important',
        position: 'absolute',
        padding: '1px 12px',
        borderRadius: '6px',
        top: '150%',
        left: '50%',
        marginLeft: '-120px',
        marginTop: '5px !important',
      },
    },
    arrow: {
      sx: {
        color: '#344054',
      },
    },
  };

  const toolTipComponentProps = {
    tooltip: {
      sx: {
        color: '#FFFFFF',
        background: '#344054',
        padding: '8px 12px',
        lineHeight: '18px',
        fontWeight: '500',
        fontSize: '12px',
        borderRadius: '6px',
        marginTop: '5px !important',
        marginRight: '-40px',
      },
    },
    arrow: {
      sx: {
        color: '#344054',
        marginLeft: '-20px',
      },
    },
  };

  const tooltipStatusProps = {
    tooltip: {
      sx: {
        color: '#FFFFFF',
        background: '#344054',
        fontWeight: '500',
        fontSize: '12px',
        borderRadius: '6px',
        marginTop: '5px !important',
      },
    },
    arrow: {
      sx: {
        color: '#344054',
      },
    },
  };

  const epicSkillStatus = data[`${propData?.sprint}_status`];

  const textWidth = React.useMemo(() => {
    let initialWidth = 0;
    if (showDelayedIcon || showUserUnavailable.includes(true)) {
      initialWidth += 16;
    }
    if (showFullStackIcon) {
      initialWidth += 16;
    }
    if (['Done', 'Blocked'].includes(epicSkillStatus)) {
      initialWidth += 16;
    }
    if (sprintStartDate && sprintDuration) {
      initialWidth += 9;
    }
    return `calc(100% - ${initialWidth}px)`;
  }, []);

  const ColId = colDef.field;
  const ColVal = data[ColId];
  const colNames = 'sprint';
  const add = '_skill';
  const ColValues = [];
  const nextValue = [];
  const noOfMaxCol = 15;
  let ValueIndex = 0;
  for (let i = 1; i <= noOfMaxCol + 2; i += 1) {
    const ColumnAndSkillNames = `${colNames}${i}${add}`;
    if (data[ColumnAndSkillNames] !== undefined) {
      const RowData = data[ColumnAndSkillNames];
      ColValues.push(i);
      nextValue.push(RowData);
      ValueIndex = i;
    }
  }
  const ID = ColId && ColId.match(/\d+/g);
  const output = () => {
    for (let j = 0; j < ColValues.length; j += 1) {
      if (ID[0] < ColValues[j]) {
        return nextValue[j];
      }
    }
    return 'Skills';
  };

  const toolTipDataForEmptyBar = React.useMemo(
    () => (
      <p>
        {`Consider adding additional ${skillKeyToSkillName(output())}`}
        {' to complete this epic sooner '}
      </p>
    ),
    [],
  );

  return (
    <div aria-rowindex={rowIndex} className="timeline-label">
      {ID && ID[0] <= ValueIndex && ColVal === undefined ? (
        <Tooltip componentsProps={toolTipEpicProps} title={toolTipDataForEmptyBar} arrow>
          <div style={{ width: '100%', height: '16px' }} />
        </Tooltip>
      ) : (
        <>
          <Tooltip
            key="tooltip-epic"
            componentsProps={toolTipEpicProps}
            title={toolTipEpicData}
            arrow
          >
            <div style={{ display: 'flex', alignItems: 'center', width: textWidth }}>
              {propData?.data[sprint]?.map(
                (user) =>
                  user[`${sprint}_userId`] && (
                    <div key={user[`${sprint}_googleId`]} className="user-assigned">
                      <AvatarImage fontSize={8} height={15} width={15} name={user[`${sprint}_displayName`]} />
                    </div>
                  ),
              )}
              <div aria-rowindex={rowIndex} data-epic={data.assignEpic} data-skill={skill} className="skillset-text">{skillName}</div>
            </div>
          </Tooltip>
          {showFullStackIcon && (
            <Tooltip
              key="tooltip"
              componentsProps={toolTipComponentProps}
              title="Full Stack Developer"
              arrow
            >
              <div className="status-icon">
                <img src={FullstackIcon} alt="full-stack" />
              </div>
            </Tooltip>
          )}
          {epicSkillStatus === 'Done' && (
            <Tooltip key="tooltipDone" componentsProps={tooltipStatusProps} title=" Done " arrow>
              <div className="status-icon">
                <img src={DoneIcon} alt="done-status" />
              </div>
            </Tooltip>
          )}
          {epicSkillStatus === 'Blocked' && (
            <Tooltip key="tooltipBlocked" componentsProps={tooltipStatusProps} title="Blocked" arrow>
              <div className="status-icon">
                <img src={BlockedIcon} alt="blocked-status" />
              </div>
            </Tooltip>
          )}
          {(showDelayedIcon || showUserUnavailable.includes(true)) && (
            <Tooltip
              key="delayed-tooltip"
              componentsProps={toolTipComponentProps}
              title={tooltipTitle}
              arrow
            >
              <div className="status-icon">
                <img src={WarningIcon} alt="delayed-warning" />
              </div>
            </Tooltip>
          )}
          {sprintStartDate && sprintDuration && value !== '' && value !== null && (
            <div className="hamburg-icon">
              <SkillStatusDialog epicSkillData={propData} />
            </div>
          )}
        </>
      )}
    </div>
  );
});

AgGridCell.propTypes = propTypes;
AgGridCell.defaultProps = defaultProps;
export default AgGridCell;
